import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Reveal } from '../components/Reveal';
import { Cambios } from '../components/Cambios';
import { Marquee } from '../components/Marquee';
import { scrollToId } from '../lib/scroll';

export default function About() {
    return (
        <main>
            <Navbar />
            <section className="pt-40 pb-20 px-6 bg-ink text-paper relative overflow-hidden" data-testid="about-hero">
                <div
                    className="absolute -top-44 -right-44 w-[520px] h-[520px] rounded-full opacity-25 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #7A1228 0%, transparent 70%)' }}
                />
                <motion.span
                    className="absolute left-8 md:left-24 bottom-14 text-paper/40 text-2xl pointer-events-none"
                    animate={{ y: [0, -12, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                >
                    ✦
                </motion.span>
                <motion.span
                    className="absolute right-16 md:right-40 bottom-24 text-paper/25 text-lg pointer-events-none"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                >
                    ✦
                </motion.span>
                <div className="relative z-10 max-w-[720px] mx-auto">
                    <Link to="/" data-testid="about-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-paper/60 hover:text-roselight transition-colors">
                        <ArrowLeft size={14} /> Volver
                    </Link>
                    <motion.h1
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 font-serif font-light text-[clamp(2.6rem,6.5vw,4.4rem)] leading-[1.08]"
                        data-testid="about-title"
                    >
                        Hola, soy <span className="italic text-roselight">Adri</span>
                    </motion.h1>
                </div>
            </section>

            <Marquee />

            <section className="py-20 px-6" data-testid="about-story">
                <div className="max-w-[640px] mx-auto text-[#4A4744] font-light text-lg leading-relaxed space-y-7">
                    <Reveal>
                        <p data-testid="about-intro">
                            Tengo 22 años y llevo 6 años en el mundo del fitness.
                        </p>
                    </Reveal>
                    <Reveal>
                        <p data-testid="about-story-inicios">
                            Desde pequeña siempre he estado practicando diferentes deportes, desde natación hasta
                            basketball en el colegio.
                        </p>
                    </Reveal>
                    <Reveal>
                        <div className="flex justify-center gap-5 md:gap-6" data-testid="about-fotos-chiquita">
                            <img
                                src="/chiquita-2.jpg"
                                alt="Adri de pequeña con sus medallas"
                                className="w-40 h-48 md:w-48 md:h-56 rounded-[16px] object-cover object-top border border-ink/10 shadow-md transition-transform duration-500 hover:scale-[1.04] hover:shadow-lg"
                            />
                            <img
                                src="/chiquita-1.jpg"
                                alt="Adri de pequeña nadando"
                                className="w-40 h-48 md:w-48 md:h-56 rounded-[16px] object-cover border border-ink/10 shadow-md transition-transform duration-500 hover:scale-[1.04] hover:shadow-lg"
                            />
                        </div>
                    </Reveal>
                    <Reveal>
                        <p data-testid="about-story-gimnasio">
                            Pero cuando conocí el gimnasio, encontré algo que se convirtió en una parte muy importante
                            de mi vida. Para mí, el gimnasio ha sido mucho más que un lugar para entrenar. Ha sido un
                            lugar al que puedo llegar, desconectarme de todo y enfocarme en mí.
                        </p>
                    </Reveal>
                    <Reveal>
                        <p data-testid="about-story-kickboxing">
                            Además del gimnasio, también practico kickboxing, y puedo decir que me encanta muchísimo.
                            Es un deporte que me ha enseñado sobre disciplina, control, constancia y sobre todo a ser
                            fuerte. Actualmente sigo entrenando y dando lo mejor de mí para certificarme, completar
                            todas mis cintas y seguir aprendiendo.
                        </p>
                    </Reveal>
                    <Reveal>
                        <figure data-testid="about-kickboxing-foto" className="group max-w-[260px] mx-auto">
                            <div className="overflow-hidden rounded-[20px] border border-ink/10 shadow-[0_2px_24px_rgba(20,20,20,0.08)] aspect-[4/5]">
                                <img
                                    src="/kickboxing-cinta.jpg"
                                    alt="Adri con su cinta de kickboxing"
                                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                                />
                            </div>
                        </figure>
                    </Reveal>
                    <Reveal>
                        <p className="font-serif italic font-medium text-2xl md:text-[1.7rem] leading-snug text-ink border-l-2 border-roselight pl-6" data-testid="about-story-hoy">
                            Y algo que quiero que sepan es que todavía no estoy donde quisiera estar, pero sé que ya no
                            estoy donde un día estuve. Sigo teniendo objetivos, sigo aprendiendo y trabajando cada día
                            para convertirme en mi mejor versión. Avanzo un día a la vez y también he aprendido a
                            celebrar cada pequeño logro que tengo.
                        </p>
                    </Reveal>
                </div>
            </section>

            <Cambios />

            <section className="py-20 px-6 border-y border-ink/10" data-testid="about-purpose">
                <Reveal className="max-w-[680px] mx-auto">
                    <p className="font-serif italic font-light text-lg md:text-xl text-ink mb-10" data-testid="about-nace">
                        Y justamente de ahí nace esto.
                    </p>
                    <p className="text-rose text-sm uppercase tracking-[0.12em] font-medium mb-6">Mi objetivo</p>
                    <p className="font-serif italic font-medium text-[clamp(1.4rem,3vw,2rem)] leading-[1.35] text-ink">
                        “Mi objetivo es ayudar a quienes quieran descubrir lo fuertes que pueden llegar a ser, no solo
                        físicamente, sino también mentalmente. Quiero que aprendan a disfrutar cada entrenamiento, a
                        entender el valor de la disciplina y sobre todo, a reconocer todo lo que pueden lograr cuando
                        deciden ser constantes y no rendirse con su proceso.”
                    </p>
                    <p className="mt-6 text-[#4A4744] font-light leading-relaxed">
                        No quiero que entrenar se convierta en una obligación en tu vida, ni que lo hagas solo por el
                        físico y ya. Quiero que encuentres en cada entrenamiento un espacio para ti. Un momento para
                        desconectarte, disfrutar, retarte y descubrir de todo lo que eres capaz.
                    </p>
                    <p className="mt-8 text-[#4A4744] font-light leading-relaxed" data-testid="about-sigo-aprendiendo">
                        Porque yo también sigo aprendiendo, sigo teniendo metas y sigo trabajando en mí. Y si algo me
                        han enseñado estos 6 años es que no necesitas ser perfecta para comenzar, solo necesitas
                        decidir que quieres avanzar.
                    </p>
                </Reveal>
            </section>

            <section className="py-24 px-6" data-testid="about-closing">
                <Reveal className="max-w-[600px] mx-auto text-center">
                    <p className="font-serif font-light text-[clamp(1.25rem,2.4vw,1.6rem)] leading-snug text-ink" data-testid="about-gracias">
                        Gracias por llegar hasta aquí y por tomarte el tiempo de conocer un poquito de mí. Ahora quiero
                        conocer un poco de ti y lo que quieres lograr. Espero que te animes y que pueda acompañarte en el proceso. 💌
                    </p>
                    <Link
                        to="/"
                        data-testid="about-cta-link"
                        onClick={() => setTimeout(() => scrollToId('#contacto'), 350)}
                        className="mt-10 inline-flex items-center gap-3 rounded-full bg-rose text-paper px-8 py-4 text-base font-medium tracking-wide hover:bg-roselight hover:-translate-y-px active:scale-95 transition"
                    >
                        Quiero comenzar <ArrowRight size={16} />
                    </Link>
                </Reveal>
            </section>

            <Footer />
        </main>
    );
}
